/**
 * ZONIY - Vendor Orders Page
 * @module VendorOrders
 * @author Sohail - Backend Developer
 * @description Vendor order management
 * @version 1.0.0
 * Git: Sohail <sohail@linesoft.com> - Order display
 */

import React, { useState, useEffect } from 'react';
import { Package, Calendar, DollarSign } from 'lucide-react';
import VendorLayout from '../../components/VendorLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/reports/sales`);
      setOrders(response.data.orders || []);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <VendorLayout>
      <div className="p-8" data-testid="vendor-orders">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Orders
        </h1>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 dark:bg-slate-800 animate-pulse" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">
              No Orders Yet
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              Orders containing your parts will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => (
              <div 
                key={order.order_id} 
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
                data-testid={`order-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono text-xs text-slate-500 uppercase tracking-wider">
                      Order #{order.order_id.slice(0, 8)}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar className="w-4 h-4" />
                        {new Date(order.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-2xl text-blue-600">
                      ${order.total.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">{order.items.length} items</div>
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="space-y-2">
                    {order.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-mono text-slate-500">{item.part_number}</span>
                          <span className="mx-2">-</span>
                          <span className="text-slate-900 dark:text-white">{item.name}</span>
                        </div>
                        <div className="text-slate-600 dark:text-slate-400">
                          {item.quantity} × ${item.price.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </VendorLayout>
  );
};

export default VendorOrders;
